import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  proDriverAgreementListQuery,
  ProDriverAgreementRecord,
} from "@/app/ProDrivers/Agreements/GraphQL/agreementQueries";
import {
  deleteProDriverAgreement,
  sendProDriverAgreement,
  voidProDriverAgreement,
} from "@/app/ProDrivers/Agreements/GraphQL/agreementMutations";
import { AgreementStatusBadge } from "@/app/ProDrivers/Agreements/Components/AgreementStatusBadge";
import { CreateAgreementDialog } from "@/app/ProDrivers/Agreements/Components/Dialogs/CreateAgreementDialog";
import { AgreementDialog } from "@/app/ProDrivers/Agreements/Components/Dialogs/AgreementDialog";
import { AgreementTimeline } from "@/app/ProDrivers/Agreements/Components/AgreementTimeline";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { ActionGroupButton } from "@/lib/Components/Actions/ActionGroupButtons";
import { Button } from "@/components/catalyst/button";
import {
  PlusIcon,
  PaperAirplaneIcon,
  TrashIcon,
  PencilSquareIcon as PencilSolidIcon,
} from "@heroicons/react/20/solid";
import { ArchiveBoxIcon } from "@heroicons/react/24/outline";
import { PDFIcon } from "@/assets/Icons";
import { ProDriverAgreementStatus } from "@/gql/graphql";
import { toast } from "sonner";

export function AgreementsSection({
  workOrderAssignmentId,
}: {
  workOrderAssignmentId: string;
}) {
  const { data } = useGqlQuery(proDriverAgreementListQuery, {
    work_order_assignment_id: workOrderAssignmentId,
  });
  const { openAsPromise: openCreateDialog } = useDialog(CreateAgreementDialog);
  const { open: openAgreementDialog } = useDialog(AgreementDialog);

  const agreements = data?.proDriverAgreements ?? [];
  // A drive is governed by one agreement at a time; the backend rejects a second.
  const hasLiveAgreement = agreements.some(
    (agreement) => agreement.status !== ProDriverAgreementStatus.Voided,
  );

  async function handleCreate() {
    const newAgreementId = await openCreateDialog({
      workOrderAssignmentId,
    }).catch(() => null);

    if (newAgreementId) {
      openAgreementDialog({ agreementId: newAgreementId });
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Agreements</h3>
        {hasLiveAgreement ? null : (
          <Button plain onClick={handleCreate}>
            <PlusIcon className="size-4" />
            Add
          </Button>
        )}
      </div>
      <div className="px-4 py-3">
        {agreements.length === 0 ? (
          <p className="text-sm text-gray-500">No agreements yet</p>
        ) : (
          <div className="space-y-3">
            {agreements.map((agreement) => (
              <AgreementRow key={agreement.id} agreement={agreement} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AgreementRow({ agreement }: { agreement: ProDriverAgreementRecord }) {
  const { open: openAgreementDialog } = useDialog(AgreementDialog);
  const { open: openPdfDialog } = useDialog(PreviewPdfDialog);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync: sendAgreement } = useGqlMutation(sendProDriverAgreement);
  const { mutateAsync: voidAgreement } = useGqlMutation(voidProDriverAgreement);
  const { mutateAsync: deleteAgreement } = useGqlMutation(
    deleteProDriverAgreement,
  );

  const isDraft = agreement.status === ProDriverAgreementStatus.Draft;
  const isSigned = agreement.status === ProDriverAgreementStatus.Signed;
  const isVoided = agreement.status === ProDriverAgreementStatus.Voided;

  const signedPdfUrl = agreement.signedPdf?.url;
  const pdfUrl = agreement.pdf?.url;

  const openAgreement = () =>
    openAgreementDialog({ agreementId: agreement.id });

  const actions: ActionGroupButton[] = [
    {
      label: "Actions",
      items: [
        {
          actions: [
            {
              label: isDraft ? "Edit" : "Open",
              Icon: PencilSolidIcon,
              handleAction: openAgreement,
            },
            {
              isHidden: isSigned || isVoided,
              label: isDraft ? "Send to driver" : "Resend to driver",
              Icon: PaperAirplaneIcon,
              handleAction: async () => {
                await sendAgreement({ id: agreement.id });
                toast.success("Agreement sent to the driver");
              },
            },
            {
              isHidden: isDraft || !pdfUrl || !!signedPdfUrl,
              label: "PDF",
              Icon: PDFIcon,
              handleAction: () => openPdfDialog({ url: pdfUrl! }),
            },
            {
              isHidden: !signedPdfUrl,
              label: "Signed PDF",
              Icon: PDFIcon,
              handleAction: () => openPdfDialog({ url: signedPdfUrl! }),
            },
            {
              isHidden: isDraft || isVoided,
              label: "Void",
              Icon: ArchiveBoxIcon,
              handleAction: async () => {
                await voidAgreement({ id: agreement.id });
                toast.success("Agreement voided");
              },
            },
            {
              isHidden: !isDraft,
              label: "Delete",
              Icon: TrashIcon,
              handleAction: () =>
                openConfirmDelete({
                  title: "Delete agreement",
                  message: "Delete this draft agreement?",
                  onDelete: async () => {
                    await deleteAgreement({ id: agreement.id });
                    toast.success("Agreement deleted");
                  },
                }),
            },
          ],
        },
      ],
    },
  ];

  return (
    <div className="flex items-center justify-between text-sm">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="truncate font-medium text-gray-900 hover:text-blue-600"
            onClick={openAgreement}
          >
            {agreement.title}
          </button>
          <AgreementStatusBadge status={agreement.status} />
        </div>
        <p className="text-xs text-gray-500">
          <AgreementTimeline agreement={agreement} />
        </p>
      </div>
      <IconMenuActions actions={actions} />
    </div>
  );
}
